import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Search, X, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";

/**
 * CrudManager: reusable admin scaffold for list + create/edit modal + delete.
 *
 * Props:
 *   title, subtitle, endpoint (e.g. "/admin/products"),
 *   fields: list of field defs, defaultItem,
 *   listRender: (item) => JSX row body,
 *   searchKeys: [strings]
 *   testId: root list test id
 */
export default function CrudManager({ title, subtitle, endpoint, fields, defaultItem, listRender, searchKeys = ["name", "title", "question"], testId, formRenderer }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // object for edit, "new" for create, null closed
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...defaultItem });
  const openEdit = (it) => setEditing({ ...it });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`${endpoint}/${editing.id}`, editing);
        toast.success("Updated.");
      } else {
        await api.post(endpoint, editing);
        toast.success("Created.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleteTarget.id}`);
      toast.success("Deleted.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = q
    ? items.filter((it) => searchKeys.some((k) => (it[k] || "").toString().toLowerCase().includes(q.toLowerCase())))
    : items;

  return (
    <div data-testid={testId} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label mb-1">Manage</p>
          <h1 className="serif-display text-3xl md:text-4xl text-foreground">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-foreground/70 max-w-2xl">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-10 w-56 rounded-sm bg-card" />
          </div>
          <Button data-testid={ADMIN.createBtn} onClick={openNew} className="h-10 rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-widest uppercase text-xs px-4">
            <Plus size={14} className="mr-2" /> New
          </Button>
        </div>
      </div>

      <CrudListBody
        loading={loading}
        items={filtered}
        q={q}
        listRender={listRender}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
      />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit" : "Create"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              {formRenderer(editing, (patch) => setEditing((prev) => ({ ...prev, ...patch })))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">
              <X size={13} className="mr-2" /> Cancel
            </Button>
            <Button data-testid={ADMIN.saveBtn} onClick={save} disabled={saving} className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold">
              {saving ? <><Loader2 size={13} className="animate-spin mr-2" /> Saving…</> : <><Save size={13} className="mr-2" /> Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CrudListBody({ loading, items, q, listRender, onEdit, onDelete }) {
  if (loading) {
    return (
      <div className="py-24 text-center text-xs tracking-widest uppercase text-foreground/60">
        <Loader2 size={16} className="inline animate-spin mr-2" />Loading…
      </div>
    );
  }
  if (items.length === 0) {
    const emptyMsg = q ? "Try clearing the search filter." : "Create your first item to get started.";
    return (
      <div className="py-24 text-center border border-dashed border-border rounded-md">
        <p className="section-label mb-2">Empty</p>
        <p className="font-serif text-2xl">No items found.</p>
        <p className="mt-2 text-sm text-foreground/60">{emptyMsg}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2">
      {items.map((it) => (
        <div key={it.id} className="group p-4 rounded-md border border-border/60 bg-card hover:border-gold/60 transition-colors flex items-center gap-4">
          <div className="flex-1 min-w-0">{listRender(it)}</div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid={`${ADMIN.editBtn}-${it.id}`}
              onClick={() => onEdit(it)}
              className="h-8 w-8 rounded-sm border border-border/60 hover:border-gold hover:text-gold text-foreground/70 flex items-center justify-center transition-colors"
              aria-label="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              data-testid={`${ADMIN.deleteBtn}-${it.id}`}
              onClick={() => onDelete(it)}
              className="h-8 w-8 rounded-sm border border-border/60 hover:border-destructive hover:text-destructive text-foreground/70 flex items-center justify-center transition-colors"
              aria-label="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
